
import { computed, defineAsyncComponent } from "vue";
import { Options, setup, Vue } from "vue-class-component";
import { useStore } from "vuex";
import { key } from "@/store";

import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faBell, faCaretDown, faCaretRight, faCaretUp, faEye, faStar } from "@fortawesome/free-solid-svg-icons";
import { useMeta } from "vue-meta";

import { Bar, Doughnut } from "vue-chartjs";
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, BarElement, CategoryScale, LinearScale } from "chart.js";

ChartJS.register( Title, Tooltip, ArcElement, Legend, BarElement, CategoryScale, LinearScale );

library.add( faEye, faStar, faBell, faCaretDown, faCaretUp, faCaretRight );

@Options( {
  components : {
    Bar,
    FontAwesomeIcon,
    Header          : defineAsyncComponent( () => import("../components/Header.vue" ) ),
    Footer          : defineAsyncComponent( () => import("../components/Footer.vue" ) ),
    Loader          : defineAsyncComponent( () => import("../components/Loader.vue" ) ),
    Map             : defineAsyncComponent( () => import("../components/Map.vue") ),
    Carousel        : defineAsyncComponent( () => import("../components/GM/Carousel.vue") ),
    Boxes           : defineAsyncComponent( () => import("../components/GM/Boxes.vue") ),
    AvisSummary     : defineAsyncComponent( () => import("../components/GM/AvisSummary.vue") ),
    AddressModal    : defineAsyncComponent( () => import("../components/GM/AddressModal.vue") ),
    AvisModal       : defineAsyncComponent( () => import("../components/GM/AvisModal.vue") ),
    ContactModal    : defineAsyncComponent( () => import("../components/GM/ContactModal.vue") ),
    CallbackModal   : defineAsyncComponent( () => import("../components/GM/CallbackModal.vue") ),
    RendezVousModal : defineAsyncComponent( () => import("../components/GM/RendezVousModal.vue") ),
    Avis            : defineAsyncComponent( () => import("../components/GM/Avis.vue") ),
    Actions         : defineAsyncComponent( () => import("../components/GM/Actions.vue") ),
    NearbyGM        : defineAsyncComponent( () => import("../components/GM/NearbyGM.vue") )
  }
} )
export default class Main extends Vue
{
  store                     = useStore( key );
  current                   = 0;
  avis_shown                = 4;
  description_shown         = false;
  faq_shown                 = false;
  show_opening              = false;
  show_access               = false;
  map_shown                 = false;
  services_with_icons : any = [];
  window                    = window;
  avisBox                   = null;

  charts : any = null;

  meta = setup( () => useMeta( computed( () => {
    if( this.store.state.gm == null )
      return {};
    else
      return {
        title       : [{
          content : "Garde meuble " + this.store.state.gm.city_url.name + " - Stockage " + this.store.state.gm.enseigne,
          vmid    : "title",
          name    : "title"
        }],
        description : {
          content : this.store.state.gm.enseigne + " garde meuble " + this.store.state.gm.city_url.name + ". Louez un box sécurisé pas cher. Promotion exclusive sur les box à " + this.store.state.gm.city_url.name + ".",
          vmid    : "description",
          name    : "description"
        }
      };
  } ) ) );

  // TODO JSON-LD

  linksNofollow( text : string )
  {
    return text.replaceAll( /<a /g, "<a rel=\"nofollow noopener\"" );
  }

  boxes( boxes : any )
  {
    return boxes.filter( ( box : any ) => typeof box.available === "undefined" || box.available > 0 );
  }

  private getPriceChartLabels()
  {
    return [this.getGMOrDefault(), this.getCityNameOrDefault(), this.getDepartementNameOrDefault(), this.getRegionNameOrDefault()];
  }

  private getPriceChartData()
  {
    if( this.store.state.gm == null )
      return [0, 0, 0, 0];

    return [
      this.store.state.gm.prices?.gm ?? 0,
      this.store.state.gm.prices?.city ?? 0,
      this.store.state.gm.prices?.departement ?? 0,
      this.store.state.gm.prices?.region ?? 0
    ];
  }

  private getSizeChartLabels()
  {
    // array keys from this.store.state.gm.prices
    let sizes = this.store.state.gm?.sizes?.stats ? Object.keys( this.store.state.gm.sizes?.stats ) : [];

    // add m² to each size
    return sizes.map( ( size ) => size + " m²" );
  }

  private getSizeChartData()
  {
    // array values from this.store.state.gm.prices
    return this.store.state.gm?.sizes?.stats ? Object.values( this.store.state.gm.sizes?.stats ) : [];
  }

  private getSizeMostWanted()
  {
    let sizes            = this.store.state.gm?.sizes?.stats ? Object.keys( this.store.state.gm.sizes?.stats ) : [];
    let most_wanted      = 0;
    let most_wanted_size = 0;

    for( let size of sizes )
    {
      if( this.store.state.gm.sizes?.stats[size] > most_wanted )
      {
        most_wanted      = this.store.state.gm.sizes?.stats[size];
        most_wanted_size = Number( size );
      }
    }

    return most_wanted_size;
  }

  getMonthYear()
  {
    let objDate = new Date();
    let month   = objDate.toLocaleString( "fr-fr", { month : "long" } );
    return month.charAt( 0 ).toUpperCase() + month.slice( 1 ) + " " + objDate.getFullYear();
  }

  getYear()
  {
    return new Date().getFullYear();
  }

  getGMOrDefault()
  {
    return this.store.state.gm?.name ?? "Centre";
  }

  getCityNameOrDefault()
  {
    return this.store.state.gm?.city?.name ?? "Ville";
  }

  getDepartementNameOrDefault()
  {

    return ("Département - " + this.store.state.gm?.city.departement.name).trim();
  }

  getRegionNameOrDefault()
  {
    return ("Région - " + this.store.state.gm?.city.region.name).trim();
  }

  jsonBoxesLD()
  {
    let boxes   = [];
    let boxlist = this.store.state.gm.boxes;

    for( const key in boxlist )
    {
      let additionalProperty = [];

      additionalProperty.push( {
        "@type" : "PropertyValue",
        "name"  : "Surface",
        "value" : boxlist[key].size + " m²"
      } );

      console.log( boxlist[key].services );
      for( const prop in boxlist[key].services )
      {
        if( boxlist[key].services[prop] !== "" )
        {
          additionalProperty.push( {
            "@type" : "PropertyValue",
            "name"  : boxlist[key].services[prop],
            "value" : "Oui"
          } );
        }
      }

      boxes.push( {
        "@type"              : "Product",
        "image"              : this.store.state.gm?.images[0]?.url,
        "description"        : boxlist[key].type_name + " " + boxlist[key].size + boxlist[key].unit,
        "mainEntityOfPage"   : {
          "@type" : "WebPage",
          "@id"   : this.store.state.gm.url
        },
        "name"               : boxlist[key].type_name + " " + boxlist[key].size + boxlist[key].unit,
        "offers"             : {
          "@type"         : "Offer",
          "price"         : boxlist[key].price.show === true ? boxlist[key].price.normal.toFixed( 2 ) : "0",
          "priceCurrency" : "EUR",
          "availability"  : "InStock"
        },
        "additionalProperty" : additionalProperty
      } );
    }

    // return as string exploded by comma
    return JSON.stringify( boxes ).replace( /,/g, ", " );
  }

  jsonServicesLD()
  {
    let services = [];
    for( const key in this.store.state.gm.services )
    {
      services.push( {
        "@type" : "PropertyValue",
        "name"  : this.store.state.gm.services[key],
        "value" : "Oui"
      } );
    }

    // return as string exploded by comma
    return JSON.stringify( services ).replace( /,/g, ", " );
  }

  mounted()
  {
    // Hide loader if page content has preloaded
    let preload = document.getElementById( "preload" );
    if( preload )
      this.store.state.loading = false;

    let headers = new Headers();

    try
    {
      fetch( this.store.state.apiVue + "gm/" + this.$route.params.gm_id, {
        method  : "GET",
        headers : headers,
        cache   : "no-cache"
      } ).then( ( res ) => {
        if( res.status == 404 )
          this.$router.push( { name : "home" } );
        return res.json();
      } ).then( ( json ) => {
        if( typeof json.error !== "undefined" )
        {
          this.store.state.error = "Une erreur est survenue lors du chargement de la page (Error " + json.errcode + ")";
          this.$router.push( { name : "home" } );
        } else
        {
          // Redirect to real URL
          if( ("https://www.location-gardemeuble.fr" + this.$route.path) != json.data.url )
          {
            window.location.replace( json.data.url.replace( "https://www.location-gardemeuble.fr/", this.store.state.base_url ) );
            return;
          }

          this.store.state.gm = json.data;
          // localStorage.setItem( "gm_id", this.store.state.gm.id );

          this.charts = {
            tarif : {
              data    : {
                labels   : this.getPriceChartLabels(),
                datasets : [
                  {
                    data            : this.getPriceChartData(),
                    backgroundColor : [
                      "rgb(50,133,58)",
                      "rgb(28,90,162)",
                      "rgb(242,159,26)",
                      "rgb(53,56,138)"
                    ]
                  }
                ]
              },
              options : {
                animations          : false,
                indexAxis           : "x",
                scales              : {
                  x : {
                    ticks : {
                      color           : "black",
                      font            : {
                        size : 13
                      },
                      autoSkip        : false,
                      autoSkipPadding : false,
                      labelOffset     : this.store.getters.isMobile ? -8 : 0,
                      maxRotation     : this.store.getters.isMobile ? 90 : 0,
                      minRotation     : this.store.getters.isMobile ? 90 : 0
                    },
                    grid  : {
                      display : false
                    }
                  }
                },
                plugins             : {
                  legend  : {
                    display : false
                  },
                  tooltip : {
                    callbacks : {
                      label : ( item : any ) =>
                        `Prix : ${item.formattedValue} € / m² en moyenne`
                    }
                  }
                },
                responsive          : true,
                maintainAspectRatio : false
              }
            },
            size  : {
              data    : {
                labels   : this.getSizeChartLabels(),
                datasets : [
                  {
                    data            : this.getSizeChartData(),
                    backgroundColor : [
                      "rgb(28,90,162)"
                    ]
                  }
                ]
              },
              options : {
                animations          : false,
                indexAxis           : "x",
                scales              : {
                  x : {
                    ticks : {
                      color           : "black",
                      font            : {
                        size : 13
                      },
                      autoSkip        : false,
                      autoSkipPadding : false,
                      labelOffset     : this.store.getters.isMobile ? -8 : 0,
                      maxRotation     : this.store.getters.isMobile ? 90 : 0,
                      minRotation     : this.store.getters.isMobile ? 90 : 0
                    },
                    grid  : {
                      display : false
                    }
                  }
                },
                plugins             : {
                  legend  : {
                    display : false
                  },
                  tooltip : {
                    callbacks : {
                      label : ( item : any ) =>
                        `${item.formattedValue}% des demandes`
                    }
                  }
                },
                responsive          : true,
                maintainAspectRatio : false
              }
            }
          };

          // Register visit count
          this.store.state.counter( "visit", this.store.state.gm.id, () => "" );

          setTimeout( () => {
            let services : any = {};
            for( const key in this.store.state.gm.services )
              services[this.store.state.gm.services[key].id] = this.store.state.gm.services[key].name;
            this.store.state.gm.services = services;
            this.services_with_icons     = ([1, 4, 5, 6, 8, 34, 39, 50, 51, 53, 55, 101, 103, 120].filter( value => Object.keys( services ).includes( value.toString() ) ));

            if( document.location.hash.startsWith( "#avis" ) )
              window.scrollTo( {
                top      : (document.getElementById( "gm-avis" )?.offsetTop ?? 0) - 150,
                behavior : "smooth"
              } );

            if( document.location.hash.startsWith( "#rdv" ) )
              this.store.state.rdv_modal = true;

            this.store.state.loading = false;

            // Remove div#preload from DOM
            let preload = document.getElementById( "preload" );
            if( preload )
            {
              window.scrollTo( { top : 0 } );
              preload.remove();
            }

          }, this.store.state.delay );
        }
      } ).catch( error => {
        this.store.state.loading = false;
        this.$router.push( { name : "home" } );
      } );
    }
    catch( e )
    {
      this.$router.push( { name : "home" } );
    }
  }
}
